// Currency configuration for flexible currency handling
export interface CurrencyConfig {
  code: string
  symbol: string
  name: string
  locale?: string
  decimalPlaces?: number
  position?: 'before' | 'after'
}

// Default currency configuration
export const DEFAULT_CURRENCY: CurrencyConfig = {
  code: 'THB',
  symbol: '฿',
  name: 'Thai Baht',
  locale: 'th-TH',
  decimalPlaces: 2,
  position: 'before',
}

/**
 * Get currency configuration from currency object (preferred method)
 * @param currencyObject - Currency object from backend
 * @returns Currency configuration
 */
export const getCurrencyConfigFromObject = (
  currencyObject?: CurrencyConfig,
): CurrencyConfig => {
  if (!currencyObject) {
    return DEFAULT_CURRENCY
  }

  return {
    code: currencyObject.code || 'THB',
    symbol: currencyObject.symbol || '฿',
    name: currencyObject.name || 'Thai Baht',
    locale: currencyObject.locale || 'th-TH',
    decimalPlaces: currencyObject.decimalPlaces || 2,
    position: currencyObject.position || 'before',
  }
}

/**
 * Format price with currency object from backend
 * @param price - Price value
 * @param currencyObject - Currency object from backend
 * @param options - Formatting options
 * @returns Formatted price string
 */
export const formatPriceWithCurrencyObject = (
  price: number,
  currencyObject?: CurrencyConfig,
  options?: {
    spaceBetweenCurrencyAndNumber?: boolean
    showSymbol?: boolean
    showCode?: boolean
    decimalPlaces?: number
  },
): string => {
  const config = getCurrencyConfigFromObject(currencyObject)
  const {
    showSymbol = true,
    showCode = false,
    decimalPlaces = config.decimalPlaces,
    spaceBetweenCurrencyAndNumber = false,
  } = options || {}

  // Format the number with comma and locale
  const formattedNumber = Number(price).toLocaleString(
    config.locale || 'en-US',
    {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    },
  )

  // Build the currency part
  let currencyPart = ''
  if (showSymbol) {
    currencyPart = config.symbol
  } else if (showCode) {
    currencyPart = config.code
  }

  // Combine based on position
  if (config.position === 'before') {
    return `${currencyPart}${spaceBetweenCurrencyAndNumber ? ' ' : ''}${formattedNumber}`
  } else {
    return `${formattedNumber}${spaceBetweenCurrencyAndNumber ? ' ' : ''}${currencyPart}`
  }
}
